import type { DrinkCue } from '../config/character.config';
import type { AudioConfig } from '../config/audio.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { HazardKind } from '../hazards/HazardDefinition';
import type { HazardInstance } from '../hazards/HazardInstance';
import { HazardCue } from '../hazards/HazardModel';
import { hazardKeyZ } from '../hazards/hazardRules';
import { AttemptGrade, MiniGameKind } from '../minigames/MiniGame';
import { clamp, clamp01 } from '../utils/math';

/** Sound ids – final files go to public/assets/audio/{sfx,music,ambience}/ (see ASSET_PIPELINE.md). */
export const SoundId = {
  Jump: 'SFX_JUMP',
  DoubleJump: 'SFX_DOUBLE_JUMP',
  Land: 'SFX_LAND',
  Slide: 'SFX_SLIDE',
  Footstep: 'SFX_FOOTSTEP',
  LaneChange: 'SFX_LANE_WHOOSH',
  Collect: 'SFX_BOTTLE_POP',
  ExtraLife: 'SFX_EXTRA_LIFE',
  /** Next level reached: rising triad and a held high chord. */
  LevelUp: 'SFX_LEVEL_UP',
  Stumble: 'SFX_STUMBLE',
  Hit: 'SFX_HIT',
  Turn: 'SFX_TURN_WHOOSH',
  Collapse: 'SFX_BUILDING_COLLAPSE',
  /** Fell into a pit: whoosh down + thud. */
  Fall: 'SFX_FALL',
  /** Jumped against a ceiling: comic knock. */
  HeadBump: 'SFX_HEAD_BUMP',
  /** Crane / excavator about to act: reversing-alarm beeps. */
  HazardWarning: 'SFX_HAZARD_WARNING',
  /** Forklift / truck about to drive off. */
  VehicleHorn: 'SFX_VEHICLE_HORN',
  /** Vehicle signals a lane change. */
  VehicleSignal: 'SFX_VEHICLE_SIGNAL',
  /** A crane load touched down, rubble was dumped. */
  HazardImpact: 'SFX_HAZARD_IMPACT',
  /** A swinging crane load turns at the end of its swing: the chain sling creaks. */
  ChainCreak: 'SFX_CHAIN_CREAK',
  /** Dozer engine: one diesel chug (idling slowly, driving faster) – loudness by distance. */
  DozerEngine: 'SFX_DOZER_ENGINE',
  /** The blade meets a heap: a scraping crunch of earth. */
  DozerPush: 'SFX_DOZER_PUSH',
  /** The pile slides into the pit: a long rumble of earth and a thud. */
  DozerFill: 'SFX_DOZER_FILL',
  GameOver: 'SFX_GAME_OVER',
  /** Extra-life drink: bottle opened ("pssst"), a gulp, a relieved "Ahh", clinking "Prost!". */
  BottleOpen: 'SFX_BOTTLE_OPEN',
  Gulp: 'SFX_GULP',
  Ahh: 'SFX_AHH',
  Clink: 'SFX_CLINK',
  /** Bau-Quiz: the question appears, the clock ticks, right / wrong. */
  QuizStart: 'SFX_QUIZ_START',
  QuizTick: 'SFX_QUIZ_TICK',
  QuizRight: 'SFX_QUIZ_RIGHT',
  QuizWrong: 'SFX_QUIZ_WRONG',
  /** Mini game hammer & nail: steel on steel (intensity 0 … 1: side hit … dead centre). */
  HammerHit: 'SFX_HAMMER_HIT',
  MusicRun: 'MUS_RUN_LOOP',
  AmbienceSite: 'AMB_CONSTRUCTION_SITE',
} as const;
export type SoundId = (typeof SoundId)[keyof typeof SoundId];

/** A running engine near the runner (dozer set piece): where it is and how fast it chugs. */
export interface EngineSound {
  /** Track distance of the engine minus the runner's (m, negative = behind). */
  readonly ahead: number;
  /** Chugs per second. */
  readonly rate: number;
  /** Not heard further away than this (m); nearer it fades in from `minLoudness`. */
  readonly range: number;
  readonly minLoudness: number;
}

/** Sound of each extra-life drink cue. */
const DRINK_SOUNDS: Readonly<Record<DrinkCue, SoundId>> = {
  open: SoundId.BottleOpen,
  gulp: SoundId.Gulp,
  ahh: SoundId.Ahh,
  prost: SoundId.Clink,
};

/**
 * Plays a sound; intensity scales the variant: bottle value of a pickup, impact speed of a
 * landing, ±1 for the left/right foot, loudness 0 … 1 of a moving hazard (by distance).
 */
export interface SoundBackend {
  play(id: SoundId, intensity: number): void;
  /** Background music on / off (effects stay); backends without music ignore it. */
  setMusicEnabled?(enabled: boolean): void;
  /** Game sounds on / off (music stays as set). */
  setEffectsEnabled?(enabled: boolean): void;
}

/**
 * Event → sound mapping plus the running footsteps (cadence from the forward speed). The backend
 * decides which sounds exist (SynthSoundBackend); the rest stays silent until final files are in.
 */
export class AudioManager {
  private lastPlayed: SoundId | null = null;
  private stepPhase = 0;
  private foot = 1;
  private lastDistance: number | null = null;
  private enginePhase = 0;

  constructor(
    events: EventBus<GameEvents>,
    private readonly config: AudioConfig,
    private readonly backend: SoundBackend | null = null,
  ) {
    events.on('player:jumped', ({ double }) => this.play(double ? SoundId.DoubleJump : SoundId.Jump));
    events.on('player:landed', ({ impactSpeed }) => this.play(SoundId.Land, impactSpeed));
    events.on('player:slideStarted', () => this.play(SoundId.Slide));
    events.on('player:laneChanged', () => this.play(SoundId.LaneChange));
    events.on('collectible:collected', ({ bottleValue }) => this.play(SoundId.Collect, bottleValue));
    events.on('score:extraLife', () => this.play(SoundId.ExtraLife));
    events.on('turbo:started', () => this.play(SoundId.LevelUp));
    events.on('player:stumbled', () => this.play(SoundId.Stumble));
    events.on('player:hit', () => this.play(SoundId.Hit));
    events.on('turn:executed', () => this.play(SoundId.Turn));
    events.on('turn:missed', () => this.play(SoundId.Hit));
    events.on('dynamicEvent:armed', ({ socket }) => {
      if (socket.segment.definition.collapseEvent?.socketId === socket.socket.id) this.play(SoundId.Collapse);
    });
    events.on('player:died', () => this.play(SoundId.GameOver));
    events.on('player:fell', () => this.play(SoundId.Fall));
    events.on('player:headBumped', () => this.play(SoundId.HeadBump));
    events.on('hazard:cue', ({ hazard, cue }) => this.hazardCue(hazard, cue));
    events.on('drink:cue', ({ cue }) => this.play(DRINK_SOUNDS[cue]));
    events.on('quiz:started', () => this.play(SoundId.QuizStart));
    events.on('quiz:tick', () => this.play(SoundId.QuizTick));
    events.on('quiz:answered', ({ correct }) => this.play(correct ? SoundId.QuizRight : SoundId.QuizWrong));
    events.on('minigame:started', () => this.play(SoundId.QuizStart));
    events.on('minigame:attempt', ({ kind, grade }) => this.miniGameAttempt(kind, grade));
    events.on('minigame:ended', ({ share }) => this.play(share >= 1 - 1e-9 ? SoundId.LevelUp : share > 0 ? SoundId.QuizRight : SoundId.QuizWrong));
    events.on('dozer:heapPushed', () => this.play(SoundId.DozerPush));
    events.on('dozer:pitReached', ({ filled }) => this.play(filled ? SoundId.DozerFill : SoundId.HazardImpact));
    // ructec-Bulli: a honk to set off, the rival's horn from behind, every crash (with the clatter of the load), the touch-down
    events.on('bus:mounted', () => this.play(SoundId.VehicleHorn));
    events.on('bus:rivalComing', () => this.play(SoundId.VehicleHorn, 0.7));
    events.on('bus:crashed', () => this.play(SoundId.HazardImpact));
    events.on('bus:rivalHit', () => this.play(SoundId.HammerHit, 0.8));
    events.on('bus:shoved', () => this.play(SoundId.Hit));
    events.on('bus:tipped', () => this.play(SoundId.Collapse));
    events.on('bus:oncomingAttack', () => this.play(SoundId.VehicleHorn));
    events.on('bus:cargoLost', () => this.play(SoundId.DozerPush));
    events.on('bus:delivered', () => this.play(SoundId.LevelUp));
    events.on('bus:landed', ({ quality }) => this.play(quality === 'CLEAN' ? SoundId.Land : SoundId.Hit));
  }

  /** A judged strike, set-down or stop of a mini game. */
  private miniGameAttempt(kind: MiniGameKind, grade: AttemptGrade): void {
    const perfect = grade === AttemptGrade.Perfect;
    if (kind === MiniGameKind.Hammer) {
      if (grade === AttemptGrade.Miss) this.play(SoundId.HazardImpact, 0.45);
      else this.play(SoundId.HammerHit, perfect ? 1 : 0.6);
    } else if (kind === MiniGameKind.Crane) {
      this.play(SoundId.HazardImpact, 0.8);
    } else {
      this.play(grade === AttemptGrade.Miss ? SoundId.QuizWrong : SoundId.QuizTick);
    }
    if (perfect) this.play(SoundId.Clink);
  }

  /** Last requested sound – visible in the debug panel. */
  get last(): SoundId | null {
    return this.lastPlayed;
  }

  /** Background music on / off – call it from the click of the toggle (browsers start audio only on a gesture). */
  setMusicEnabled(enabled: boolean): void {
    this.backend?.setMusicEnabled?.(enabled);
  }

  /** Game sounds on / off – the third step of the sound switch. */
  setEffectsEnabled(enabled: boolean): void {
    this.backend?.setEffectsEnabled?.(enabled);
  }

  play(id: SoundId, intensity = 1): void {
    this.lastPlayed = id;
    this.backend?.play(id, intensity);
  }

  /**
   * Per frame: footsteps while the runner is on the ground. The cadence follows the speed
   * (stride length) but stays within what still sounds like running.
   */
  update(dt: number, distance: number, runningOnGround: boolean): void {
    const speed = this.lastDistance === null || dt <= 0 ? 0 : (distance - this.lastDistance) / dt;
    this.lastDistance = distance;
    if (!runningOnGround || speed <= 0) {
      this.stepPhase = 0;
      return;
    }
    const { strideLength, minStepsPerSecond, maxStepsPerSecond } = this.config.footsteps;
    this.stepPhase += dt * clamp(speed / strideLength, minStepsPerSecond, maxStepsPerSecond);
    if (this.stepPhase < 1) return;
    this.stepPhase -= Math.floor(this.stepPhase);
    this.foot = -this.foot;
    this.play(SoundId.Footstep, this.foot);
  }

  /** Per frame: the chugs of the nearest running engine (none: silence). `dt` 0 while paused. */
  updateEngine(dt: number, engine: EngineSound | null): void {
    if (!engine || dt <= 0 || Math.abs(engine.ahead) > engine.range) {
      if (!engine) this.enginePhase = 0;
      return;
    }
    this.enginePhase += dt * engine.rate;
    if (this.enginePhase < 1) return;
    this.enginePhase -= Math.floor(this.enginePhase);
    this.play(SoundId.DozerEngine, engine.minLoudness + (1 - engine.minLoudness) * clamp01(1 - Math.abs(engine.ahead) / engine.range));
  }

  /** Moving hazards ahead: warning beeps / horn, lane-change signal, impact – quieter with distance. */
  private hazardCue(hazard: HazardInstance, cue: HazardCue): void {
    const { range, minLoudness } = this.config.hazards;
    const ahead = hazard.segment.startDistance + hazardKeyZ(hazard.definition) - (this.lastDistance ?? 0);
    if (ahead > range) return;
    const loudness = minLoudness + (1 - minLoudness) * clamp01(1 - Math.abs(ahead) / range);
    const vehicle = hazard.definition.kind === HazardKind.Vehicle;
    switch (cue) {
      case HazardCue.Warning:
        this.play(vehicle ? SoundId.VehicleHorn : SoundId.HazardWarning, loudness);
        break;
      case HazardCue.Signal:
        this.play(SoundId.VehicleSignal, loudness);
        break;
      case HazardCue.Impact:
        this.play(SoundId.HazardImpact, loudness);
        break;
      case HazardCue.Creak:
        this.play(SoundId.ChainCreak, loudness);
        break;
    }
  }

  /** New run / restart: no speed spike from the jump back to distance 0. */
  reset(): void {
    this.lastDistance = null;
    this.stepPhase = 0;
    this.enginePhase = 0;
  }
}
